import { FC, useState } from 'react';
import { isError } from '@belong/api';
import { AUTOCOMPLETE_TYPE, useAlert } from '@belong/contentful';
import { logger } from '@belong/logging/logger';
import { BUTTON_WIDTHS } from '@belong/themes';
import { IErrorResponse, TEXT_NODES } from '@belong/types';
import { ALERT_POSITION } from '@belong/types/alert';
import { ILoginRequest, ILoginResponse } from '@belong/types/api/auth';
import {
  Alert,
  AsyncSubmitEventHandler,
  EmailInput,
  FormButton,
  GenericFormLayout,
  GenericPasswordInput,
  LIB_INPUTS_DEFAULT_IDS,
  RichText,
  useForm
} from '@belong/ui-components';
import { Footer } from '@belong/ui-components/components/layout/FormCard';
import InputsWrapper from '@belong/ui-components/components/layout/InputsWrapper';
import { MYACCOUNT_INTERNAL_ROUTES } from '@belong/url-utils';
import { useProspectUserEmail } from '../../hooks';
import { ButtonLinkWrapper } from './FormLogin.styles';
import { CREATE_ACCOUNT_LINK, FORGOT_PASSWORD_LINK, FORM_ROOT, LOGIN_BUTTON } from './test-ids';
import { ILoginFormContent, TLoginHeadingVariant } from './types';

const buttonStyles = {
  xs: BUTTON_WIDTHS.full,
  md: BUTTON_WIDTHS.min
};

export interface IFormLoginProps {
  onLoginSubmit: (values: ILoginRequest) => Promise<IErrorResponse | ILoginResponse>;
  isStoreBusy: boolean;
  destinationOverrideUrl?: string;
  initialAlertReason?: string;
  headingVariant: TLoginHeadingVariant;
  content: ILoginFormContent;
}

type Inputs = typeof LIB_INPUTS_DEFAULT_IDS.EMAIL | typeof LIB_INPUTS_DEFAULT_IDS.PASSWORD;

export const FormLogin: FC<IFormLoginProps> = ({
  onLoginSubmit,
  isStoreBusy,
  destinationOverrideUrl,
  initialAlertReason,
  headingVariant,
  content
}) => {
  /**
   * Load the page with an error alert if one has been passed in.
   * Can be updated in response to form / API errors after interaction.
   */
  const initialAlertErrorCode = initialAlertReason?.toString().toUpperCase();
  const [alertCode, setAlertCode] = useState(initialAlertErrorCode);
  const [isInitialError, setIsInitialError] = useState(!!initialAlertErrorCode);
  const { data: alertMessage } = useAlert(alertCode);
  const { initialEmail } = useProspectUserEmail(true);

  const btnRedirect = destinationOverrideUrl ? { redirect: destinationOverrideUrl } : undefined;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { errors, manageForm, FormRef } = useForm();

  const onSubmit: AsyncSubmitEventHandler = async () => {
    setAlertCode(undefined);
    setIsInitialError(false);
    setIsLoading(true);

    const { email, password } = manageForm.getData<Inputs>();

    const response = await onLoginSubmit({ email, password });
    logger.debug('result from API: ', response);

    if (isError(response)) {
      setIsLoading(false);

      /*
       * Set CustomerBlock error message which matches description that returns from the API, with the error
       * defined in contentful. This is to Block Customer from login during migration in progress.
       */
      setAlertCode(
        response.errorDescription === 'migration_in_progress' ? response.errorDescription : response.errorCode
      );
    }
  };

  return (
    <GenericFormLayout
      FormRef={FormRef}
      handleSubmit={onSubmit}
      isLoading={isLoading}
      validateForm={manageForm.validateForm}
      data-testid={FORM_ROOT}
      useButtonSpinner
      name="login"
    >
      <RichText
        spacing="large"
        alignment="center"
        h1={TEXT_NODES.headingLarge}
        html={content[headingVariant].html}
        dictionary={{ ...btnRedirect, testId: CREATE_ACCOUNT_LINK }}
      />

      {alertMessage && (
        <Alert type={alertMessage.level} position={ALERT_POSITION.BLOCK} hasFocus={!isInitialError}>
          <RichText {...alertMessage.message} />
        </Alert>
      )}

      <InputsWrapper>
        <EmailInput
          hasFormError={errors.hasFormError}
          manageForm={manageForm}
          initialValue={initialEmail}
          width="full"
          label={content.emailInput.label}
          errorMessages={content.emailInput.errorMessages}
        />
        <GenericPasswordInput
          {...content.passwordInput}
          autoComplete={AUTOCOMPLETE_TYPE.CURRENT_PASSWORD}
          data-testid={LIB_INPUTS_DEFAULT_IDS.PASSWORD}
          // Remove default description
          description=""
          hasFormError={errors.hasFormError}
          manageForm={manageForm}
          name={LIB_INPUTS_DEFAULT_IDS.PASSWORD}
          width="full"
          showLabel={content.showPasswordLabel}
          hideLabel={content.hidePasswordLabel}
        />
      </InputsWrapper>

      <Footer>
        <FormButton
          type="submit"
          variant="primary"
          data-testid={LOGIN_BUTTON}
          width={buttonStyles}
          disabled={isStoreBusy}
        >
          {content.submitButtonLabel}
        </FormButton>
        <ButtonLinkWrapper
          variant="quaternary"
          width={buttonStyles}
          href={MYACCOUNT_INTERNAL_ROUTES.TROUBLE_LOGGING_IN}
          data-testid={FORGOT_PASSWORD_LINK}
        >
          {content.forgotPasswordLabel}
        </ButtonLinkWrapper>
      </Footer>
    </GenericFormLayout>
  );
};
